import type {
  CollisionLoadOptions,
  CollisionMeshResult,
  CoordinateSystem,
} from '@mindcloudxai/shared';
import * as THREE from 'three';
import { CollisionMeshLoader } from '../CollisionMeshLoader';
import { MeshCollisionWorld } from './MeshCollisionWorld';
import { applyCollisionDebugMaterial } from './debugVisualize';

/**
 * Babylon 左手坐标系 → Three.js 右手坐标系转换。
 * Babylon: Y-up, Z 朝屏幕内（左手）
 * Three:   Y-up, Z 朝观察者（右手）
 * 转换：翻转 Z 轴（scale.z = -1），同时需要翻转面法线顺序。
 */
function applyCoordinateConversion(group: THREE.Group, source: CoordinateSystem): void {
  if (source === 'three') return;

  // Babylon → Three: 翻转 Z 轴
  group.scale.z = -1;
  // 更新世界矩阵以确保 BVH 构建使用正确的顶点位置
  group.updateMatrixWorld(true);

  // 翻转 Z 后三角面顺序反转，需要在 geometry 层修正
  group.traverse((child) => {
    if (child instanceof THREE.Mesh && child.geometry) {
      const index = child.geometry.index;
      if (index) {
        // 反转三角形绕序
        const arr = index.array as Uint16Array | Uint32Array;
        for (let i = 0; i < arr.length; i += 3) {
          const tmp = arr[i];
          arr[i] = arr[i + 2];
          arr[i + 2] = tmp;
        }
        index.needsUpdate = true;
      }
      // 将 scale 烘焙进顶点，使 BVH 在 world space 正确
      child.geometry.applyMatrix4(child.matrixWorld);
      child.position.set(0, 0, 0);
      child.rotation.set(0, 0, 0);
      child.scale.set(1, 1, 1);
      child.updateMatrixWorld(true);
    }
  });

  // 根 group 恢复为 identity（已烘焙进子 mesh）
  group.scale.set(1, 1, 1);
  group.updateMatrixWorld(true);
}

/**
 * 加载碰撞 GLB 模型并构建碰撞世界。
 *
 * 封装 three / three-mesh-bvh 细节，业务层只通过 CollisionMeshResult 交互。
 * - result.world: 碰撞世界实例（snapToGround / move / raycast）
 * - result.native: THREE.Group，用于 addObject 调试可视化
 */
export async function loadCollisionMesh(
  options: CollisionLoadOptions,
): Promise<CollisionMeshResult> {
  const loader = new CollisionMeshLoader();
  const { group, meshes } = await loader.load(options.url);

  // 坐标系转换（默认产物为 Babylon 坐标系）
  const coordSys = options.coordinateSystem ?? 'babylon';
  applyCoordinateConversion(group, coordSys);

  if (options.debug) {
    applyCollisionDebugMaterial(group);
  }

  const world = new MeshCollisionWorld(meshes, {
    radius: options.capsuleRadius,
    maxStepHeight: options.maxStepHeight,
  });

  return {
    native: group,
    world,
    meshCount: meshes.length,
  };
}
